import { logger, createLogger } from '@/utils/logger'
import { NetworkError, isNetworkError, shouldRetryRequest, getRetryDelay } from './networkErrorHandler'

const fileLogger = createLogger('retryHandler')

export interface RetryOptions {
  maxRetries?: number
  baseDelay?: number
  maxDelay?: number
  retryCondition?: (error: any, attempt: number) => boolean
  onRetry?: (error: any, attempt: number) => void
}

export class RetryHandler {
  private static instance: RetryHandler
  private retryQueues: Map<string, Array<() => Promise<any>>> = new Map()
  private retryCounts: Map<string, number> = new Map()

  static getInstance(): RetryHandler {
    if (!RetryHandler.instance) {
      RetryHandler.instance = new RetryHandler()
    }
    return RetryHandler.instance
  }

  async executeWithRetry<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {},
    operationId?: string
  ): Promise<T> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 30000,
      retryCondition = shouldRetryRequest,
      onRetry
    } = options

    const id = operationId || `operation_${Date.now()}_${Math.random()}`
    let lastError: any

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await operation()
        
        // Success - reset retry count and return result
        this.retryCounts.delete(id)
        fileLogger.log(`Operation ${id} succeeded on attempt ${attempt + 1}`)
        return result
        
      } catch (error) {
        lastError = error
        
        // Check if we should retry
        if (attempt < maxRetries && retryCondition(error, attempt)) {
          const delay = this.calculateDelay(attempt, baseDelay, maxDelay, error)
          
          fileLogger.warn(`Operation ${id} failed on attempt ${attempt + 1}, retrying in ${delay}ms:`, error)
          
          if (onRetry) {
            onRetry(error, attempt)
          }
          
          // Wait before retrying
          await this.delay(delay)
          continue
        }
        
        // No more retries or shouldn't retry
        this.retryCounts.delete(id)
        fileLogger.error(`Operation ${id} failed after ${attempt + 1} attempts:`, error)
        throw error
      }
    }
    
    throw lastError
  }

  private calculateDelay(attempt: number, baseDelay: number, maxDelay: number, error: any): number {
    if (isNetworkError(error)) {
      return Math.min(getRetryDelay(error, attempt), maxDelay)
    }
    
    // Exponential backoff with jitter
    const exponentialDelay = baseDelay * Math.pow(2, attempt)
    const jitter = Math.random() * 0.1 * exponentialDelay // 10% jitter
    return Math.min(exponentialDelay + jitter, maxDelay)
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // Queue operations for retry when network comes back online
  queueForRetry(operation: () => Promise<any>, operationId: string): void {
    if (!this.retryQueues.has(operationId)) {
      this.retryQueues.set(operationId, [])
    }
    
    this.retryQueues.get(operationId)!.push(operation)
    fileLogger.log(`Queued operation ${operationId} for retry when network is available`)
  }

  // Process queued operations when network comes back online
  async processQueuedOperations(): Promise<void> {
    fileLogger.log('Processing queued operations...')
    
    for (const [operationId, operations] of this.retryQueues.entries()) {
      if (operations.length === 0) continue
      
      fileLogger.log(`Processing ${operations.length} queued operations for ${operationId}`)
      
      // Process operations in parallel (but limit concurrency)
      const batchSize = 5
      for (let i = 0; i < operations.length; i += batchSize) {
        const batch = operations.slice(i, i + batchSize)
        
        try {
          await Promise.allSettled(batch.map(op => op()))
        } catch (error) {
          fileLogger.error(`Error processing batch for ${operationId}:`, error)
        }
      }
      
      // Clear processed operations
      this.retryQueues.set(operationId, [])
    }
  }

  // Clear all queued operations
  clearQueuedOperations(): void {
    this.retryQueues.clear()
    this.retryCounts.clear()
    fileLogger.log('Cleared all queued operations')
  }

  // Get retry statistics
  getRetryStats(): { queuedOperations: number; retryCounts: Record<string, number> } {
    const queuedOperations = Array.from(this.retryQueues.values())
      .reduce((total, ops) => total + ops.length, 0)
    
    const retryCounts = Object.fromEntries(this.retryCounts.entries())
    
    return { queuedOperations, retryCounts }
  }
}

// Convenience function for simple retry operations
export async function withRetry<T>(
  operation: () => Promise<T>,
  options?: RetryOptions
): Promise<T> {
  return RetryHandler.getInstance().executeWithRetry(operation, options)
}

// Global retry handler instance
export const retryHandler = RetryHandler.getInstance()
